import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Users, Briefcase, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import { agentBenefits, companyInfo } from '../utils/mockData';
import { submitAgentApplication } from '../utils/api';
import { toast } from 'sonner';

const Agents = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await submitAgentApplication(formData);
      if (result.success) {
        toast.success('Application submitted successfully!');
        setTimeout(() => navigate('/thank-you'), 2000);
      }
    } catch (error) {
      toast.error('Failed to submit application. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative bg-cover bg-center py-14"
        style={{
          backgroundImage: 'linear-gradient(rgba(231, 119, 34, 0.9), rgba(231, 119, 34, 0.9)), url(https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Build Your Career With Purpose</h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto">Join Purposefully Covered Insurance Group and help families secure their financial future while building your own successful insurance business</p>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">Why Join Our Team?</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              We're looking for motivated individuals who want to make a real difference in people's lives
              while building a rewarding career in the insurance industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {agentBenefits.map((benefit) => (
              <div key={benefit.id} className="text-center">
                <div className="bg-brand-blue text-white w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto">
                  {benefit.icon === 'book' && <Book size={36} />}
                  {benefit.icon === 'users' && <Users size={36} />}
                  {benefit.icon === 'briefcase' && <Briefcase size={36} />}
                  {benefit.icon === 'trending-up' && <TrendingUp size={36} />}
                </div>
                <h3 className="text-lg font-bold text-brand-blue mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Provide */}
      <section className="py-10 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-8 text-center">What We Provide</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: "Licensing Support", desc: "We guide you through the licensing process and provide resources to help you obtain your insurance license." },
                { title: "Comprehensive Training", desc: "From product knowledge to sales techniques, we provide ongoing training to help you succeed." },
                { title: "Mentorship Program", desc: "One-on-one guidance from experienced agents who are invested in your success." },
                { title: "Carrier Access", desc: "Access to multiple insurance carriers and product lines to serve diverse client needs." },
                { title: "Lead Support", desc: "Lead support when available, plus proven prospecting methods to build your client base." },
                { title: "Agent Portal Tools", desc: "Technology platform with activity tracking, resources, and business management tools." },
              ].map(item => (
                <div key={item.title} className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="text-brand-orange flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-bold text-lg text-brand-blue mb-2">{item.title}</h3>
                      <p className="text-gray-700 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Path */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-12 text-center">Your Career Path</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: 1, title: "Apply & Interview", desc: "Submit your application and complete an interview" },
                { step: 2, title: "Get Licensed", desc: "Complete pre-licensing education and pass your state exam" },
                { step: 3, title: "Training Program", desc: "Learn product knowledge and sales skills with mentors" },
                { step: 4, title: "Start Selling", desc: "Begin serving clients and building your business" },
              ].map(item => (
                <div key={item.step} className="text-center">
                  <div className="bg-brand-orange text-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-lg text-brand-blue mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-10 bg-gradient-to-r from-brand-blue to-blue-700">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-2xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-brand-blue mb-3">Apply to Join Our Team</h2>
              <p className="text-gray-600">Start your journey to a rewarding insurance career</p>
            </div>

            <form onSubmit={handleSubmit} data-testid="agent-application-form">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text" required value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder="Your full name"
                    data-testid="agent-app-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email" required value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder="you@email.com"
                    data-testid="agent-app-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel" required value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder="(912) 376-9006"
                    data-testid="agent-app-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tell Us About Yourself</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder="Why are you interested in joining our team?"
                    data-testid="agent-app-message"
                  />
                </div>
                <button
                  type="submit" disabled={isSubmitting}
                  className="w-full bg-brand-orange text-white px-6 py-4 rounded-md hover:bg-brand-orange-light transition font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="agent-app-submit"
                >
                  {isSubmitting ? 'Submitting...' : 'Apply to Join Our Team'}
                </button>
                <p className="text-center text-xs text-gray-500 mt-4">
                  By submitting this form, you agree to be contacted by Purposefully Covered Insurance Group regarding agent opportunities.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-6">Ready to Make a Difference?</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Join Purposefully Covered Insurance Group, a team that values integrity, supports your growth, and celebrates your success.
          </p>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: document.querySelector('[data-testid="agent-application-form"]').offsetTop - 100, behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 bg-brand-blue text-white px-8 py-3 rounded-md hover:bg-brand-orange transition font-semibold group"
          >
            Apply Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Agents;
